
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowLeft, ArrowRight, PlayCircle, ExternalLink } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TikTokLives = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const handleOpenXSpace = () => {
    window.open("https://x.com/i/spaces/1MYxNwbBQyPKw", "_blank");
  };

  const categories = [
    { id: "all", label: isRTL ? "الكل" : "All" },
    { id: "entertainment", label: isRTL ? "ترفيه" : "Entertainment" },
    { id: "religious", label: isRTL ? "ديني" : "Religious" },
    { id: "educational", label: isRTL ? "تعليمي" : "Educational" },
    { id: "music", label: isRTL ? "موسيقى" : "Music" },
    { id: "challenges", label: isRTL ? "تحديات" : "Challenges" },
    { id: "x_spaces", label: isRTL ? "استماع مباشر" : "Live Listening" },
  ];

  const videos = [
    {
      id: "7341484473605987590",
      username: "kingabdullah.c",
      category: "entertainment"
    },
    {
      id: "7342074810239917313",
      username: "kingabdullah.c",
      category: "entertainment"
    },
    {
      id: "7471369420540087573",
      username: "3dn.1",
      category: "entertainment"
    },
    {
      id: "7471322422252178689",
      username: "abuamsl",
      category: "entertainment"
    }
  ];

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.tiktok.com/embed.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const filteredVideos = videos.filter(video => 
    selectedCategory === "all" || video.category === selectedCategory
  );

  return (
    <div className={`min-h-screen bg-gray-50 py-12 ${isRTL ? "rtl" : "ltr"}`}>
      <div className="container mx-auto px-4 space-y-8">
        <div className="flex justify-start mb-4">
          <Button
            variant="ghost"
            className="flex items-center gap-2 hover:bg-gray-100"
            onClick={() => navigate('/')}
          >
            {isRTL ? (
              <>
                العودة للرئيسية
                <ArrowRight className="w-4 h-4" />
              </>
            ) : (
              <>
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </>
            )}
          </Button>
        </div>

        <Alert className="max-w-6xl mx-auto">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="mb-4 text-xl font-semibold">
            {isRTL ? "خدمة في طور التجربة" : "Service Under Testing"}
          </AlertTitle>
          <AlertDescription className="text-gray-700 leading-relaxed">
            {isRTL ? (
              <p className="text-lg space-y-4">
                هذه الخدمة لا تزال في طور التجربة، حيث يتم اختيار البثوث بعناية لضمان تقديم محتوى هادف ومفيد.
                <br /><br />
                كما نحرص على الالتزام بالقيم الإسلامية والأخلاقية، ولا نتحمل مسؤولية متابعة أي بث يخالف تعاليم الدين الإسلامي أو يتنافى مع المبادئ الأخلاقية.
              </p>
            ) : (
              <p className="text-lg space-y-4">
                This service is still under testing, where broadcasts are carefully selected to ensure meaningful and useful content.
                <br /><br />
                We are committed to Islamic and moral values, and we are not responsible for following any broadcast that violates Islamic teachings or contradicts moral principles.
              </p>
            )}
          </AlertDescription>
        </Alert>

        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isRTL ? "تصنيف البث" : "Stream Category"}
            </label>
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue placeholder={isRTL ? "اختر التصنيف" : "Select category"} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedCategory === 'x_spaces' ? (
            <div className="bg-white rounded-xl shadow-sm p-6 max-w-3xl mx-auto">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold mb-4">
                  {isRTL ? "حلقة نقاش: قواعد البيانات وعلوم البيانات" : "Discussion: Databases and Data Science"}
                </h3>
                <p className="text-gray-600 mb-6">
                  {isRTL 
                    ? "انضم إلينا في حلقة نقاش مباشرة حول قواعد البيانات وعلوم البيانات مع خبراء في المجال" 
                    : "Join us in a live discussion about databases and data science with field experts"}
                </p>
                <Button
                  onClick={handleOpenXSpace}
                  className="mx-auto flex items-center gap-2 text-lg"
                  size="lg"
                >
                  <ExternalLink className="w-5 h-5" />
                  {isRTL ? "استمع مباشرة" : "Listen Live"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredVideos.map((video) => (
                <div key={video.id} className="bg-white rounded-xl shadow-sm p-4">
                  <div className="aspect-[9/16]">
                    <blockquote 
                      className="tiktok-embed h-full w-full" 
                      cite={`https://www.tiktok.com/@${video.username}/video/${video.id}`}
                      data-video-id={video.id}>
                      <section></section>
                    </blockquote>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TikTokLives;
